import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Gift } from "lucide-react";
import Logo from "@/components/Logo";
import { useSEO } from "@/hooks/useSEO";

export default function Register() {
  const { register } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  useSEO({
    title: lang === "es"
      ? "Crear cuenta gratis — ChefSync P&L | Software financiero para hostelería"
      : "Create a free account — ChefSync P&L | Hospitality finance software",
    description: lang === "es"
      ? "Regístrate gratis en ChefSync P&L. 200 documentos y 1 mes de prueba sin coste con OCR de IA, escandallos, control de food cost y coste de personal. Sin tarjeta."
      : "Sign up free to ChefSync P&L. 200 documents and a 1-month trial with AI OCR, dish costing, food-cost and payroll tracking. No credit card required.",
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  // Iter 63 · manually-typed referral code (if the user didn't arrive via
  // ?ref=). Prefilled from the URL if present; user can still edit.
  const [manualRefCode, setManualRefCode] = useState(refCode);
  // iter 69 — "¿Cómo nos has conocido?" survey (optional).
  const [referralSource, setReferralSource] = useState("");

  const REFERRAL_SOURCES = lang === "es"
    ? [
        { v: "google", l: "Búsqueda en Google" },
        { v: "friend", l: "Un amigo / conocido" },
        { v: "colleague", l: "Compañero de sector (hostelería)" },
        { v: "social", l: "Redes sociales (Instagram, TikTok, etc.)" },
        { v: "linkedin", l: "LinkedIn" },
        { v: "blog", l: "Blog o artículo" },
        { v: "ad", l: "Anuncio" },
        { v: "event", l: "Feria o evento" },
        { v: "other", l: "Otro" },
      ]
    : [
        { v: "google", l: "Google search" },
        { v: "friend", l: "A friend / acquaintance" },
        { v: "colleague", l: "Someone in hospitality" },
        { v: "social", l: "Social media (Instagram, TikTok, etc.)" },
        { v: "linkedin", l: "LinkedIn" },
        { v: "blog", l: "Blog or article" },
        { v: "ad", l: "Advertising" },
        { v: "event", l: "Trade fair / event" },
        { v: "other", l: "Other" },
      ];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name, referralSource || null);
      // Seed default categories
      try {
        await import("@/lib/api").then((m) => m.default.post("/seed/categories"));
      } catch (err) {
        // Seeding is best-effort; surface the issue in the console without blocking signup.
        console.error("Failed to seed default categories on signup:", err);
      }
      // Iter 63 — attach referral code if present (URL param OR manual field).
      // Best-effort: don't block signup if the code is invalid or already used.
      const codeToRedeem = (manualRefCode || refCode || "").trim();
      if (codeToRedeem) {
        try {
          await import("@/lib/api").then((m) => m.default.post("/referrals/redeem", { code: codeToRedeem }));
          toast.success(lang === "es" ? "Código de invitación aplicado" : "Invitation code applied");
        } catch (err) {
          console.error("Referral redeem failed:", err);
          toast.info(lang === "es"
            ? "El código de invitación no era válido, pero tu cuenta se creó correctamente."
            : "The invitation code wasn't valid, but your account was created.");
        }
      }
      toast.success(t("welcome"));
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="w-full lg:w-[480px] xl:w-[520px] flex flex-col p-8 lg:p-12 bg-[var(--surface)]">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={36} />
            <span className="font-display font-bold text-lg">ChefSync <span className="text-[var(--text-muted)] font-normal">P&amp;L</span></span>
          </Link>
          <button
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className="text-xs uppercase tracking-wider text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-sm">
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-3">
            {t("register")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none mb-2">
            {t("createAccount")}.
          </h1>
          <p className="text-[var(--text-secondary)] mb-10 text-sm">{t("tagline")}</p>

          {refCode && (
            <div
              className="mb-6 rounded-md border border-[var(--brand-accent)]/30 bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] px-3 py-2 text-xs flex items-center gap-2"
              data-testid="register-ref-banner"
            >
              <Gift size={14} />
              <span>
                {lang === "es"
                  ? `Te ha invitado un usuario (código ${refCode.slice(0, 6)}…). Se aplicará tras registrarte.`
                  : `You were invited (code ${refCode.slice(0, 6)}…). It will be applied on signup.`}
              </span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("name")}</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="h-11"
                placeholder={lang === "es" ? "Tu nombre" : "Your name"}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
                className="h-11"
                placeholder="you@email.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("password")}</Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                  className="h-11 pr-11"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                  aria-label={showPwd ? (lang === "es" ? "Ocultar contraseña" : "Hide password") : (lang === "es" ? "Mostrar contraseña" : "Show password")}
                  data-testid="register-toggle-pwd"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {/* Iter 63 — Optional invitation code field. Prefilled from ?ref=
                URL param when present. */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-1.5">
                <Gift size={12} className="text-[var(--brand-accent)]" />
                {lang === "es" ? "Código de invitación (opcional)" : "Invitation code (optional)"}
              </Label>
              <Input
                value={manualRefCode}
                onChange={(e) => setManualRefCode(e.target.value)}
                data-testid="register-ref-code-input"
                className="h-11 font-mono-num"
                placeholder={lang === "es" ? "Si alguien te invitó, pégalo aquí" : "Paste it here if someone invited you"}
              />
            </div>
            {/* Iter 69 — Referral source survey (optional). */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
                {lang === "es" ? "¿Cómo nos has conocido? (opcional)" : "How did you hear about us? (optional)"}
              </Label>
              <select
                value={referralSource}
                onChange={(e) => setReferralSource(e.target.value)}
                data-testid="register-referral-source"
                className="w-full h-11 rounded-md border border-[var(--border-color)] bg-[var(--surface)] px-3 text-sm"
              >
                <option value="">{lang === "es" ? "— Selecciona una opción —" : "— Select an option —"}</option>
                {REFERRAL_SOURCES.map((o) => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full h-11 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            >
              {loading ? "..." : t("createAccount")}
            </Button>
          </form>

          <p className="mt-8 text-sm text-[var(--text-secondary)]">
            {t("alreadyHaveAccount")}{" "}
            <Link to="/login" data-testid="link-login" className="text-[var(--primary)] font-medium hover:underline">
              {t("login")}
            </Link>
          </p>
        </div>
      </div>

      <div
        className="hidden lg:flex flex-1 relative items-end p-12"
        style={{
          backgroundImage:
            "linear-gradient(to bottom right, rgba(44,76,59,0.85), rgba(18,18,18,0.5)), url('https://images.unsplash.com/photo-1637325258040-d2f09636ecf6?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="text-white max-w-md">
          <p className="text-xs uppercase tracking-[0.15em] opacity-70 mb-4">{lang === "es" ? "Empieza ya" : "Get started"}</p>
          <h2 className="font-display text-4xl font-bold leading-tight mb-3">
            {lang === "es" ? "Tus finanzas, ordenadas." : "Your finances, organized."}
          </h2>
          <p className="text-sm opacity-80">
            {lang === "es"
              ? "Crea tu cuenta gratis y empieza a subir facturas en segundos."
              : "Create your free account and start uploading invoices in seconds."}
          </p>
        </div>
      </div>
    </div>
  );
}
